import { router } from "expo-router";
import { ScrollView, View } from "react-native";
import { AuthForm } from "~/components/composed/AuthForm";
import { DeleteAccount } from "~/components/composed/DeleteAccount";
import { ExportData } from "~/components/composed/ExportData";
import { TwoFactorCard } from "~/components/composed/TwoFactorCard";
import { ManageSubscription } from "~/components/composed/ManageSubscription";
import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import { AppVersionPanel } from "~/lib/app-updates";
import { Button } from "~/components/ui/Button";
import { ModalHeader } from "~/components/ui/ModalHeader";
import { RuleLine } from "~/components/ui/RuleLine";
import { Screen } from "~/components/ui/Screen";
import { CaptionText, Heading3 } from "~/components/ui/Text";
import { useAuth } from "~/hooks/useAuth";
import {
  DURANCE_MONTHLY_PRICE_TEXT,
  DURANCE_TRIAL_DAYS,
} from "~/services/revenuecat";
import { spacing, useTheme } from "~/styles/theme";

export default function AccountScreen() {
  const { colors } = useTheme();
  const { user, configured, error: authError, signOut } = useAuth();

  if (user) {
    return (
      <Screen>
        <ModalHeader title="Account" onClose={() => router.back()} />
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.lg, gap: spacing.lg }}
        >
          <View style={{ gap: spacing.xxs }}>
            <Heading3>{user.name}</Heading3>
            <CaptionText color={colors.muted}>{user.email}</CaptionText>
          </View>
          <RuleLine />
          {user.hasSyncAccess ? (
            <View style={{ gap: spacing.md }}>
              <CaptionText color={colors.muted}>
                Your counters sync to every device you sign in on.
              </CaptionText>
              <ManageSubscription />
            </View>
          ) : (
            <UpgradePrompt />
          )}
          <RuleLine />
          <Button
            label="Support"
            variant="secondary"
            fullWidth
            onPress={() => router.push("/support" as never)}
          />
          <Button
            label="Sign out"
            variant="secondary"
            fullWidth
            onPress={async () => {
              await signOut();
              router.back();
            }}
          />
          <RuleLine />
          <TwoFactorCard />
          <RuleLine />
          <ExportData />
          <RuleLine />
          <DeleteAccount />
          <RuleLine />
          <AppVersionPanel
            colors={{
              text: colors.ink,
              muted: colors.muted,
              border: colors.rule,
              accent: colors.accent,
              onAccent: colors.accentInverse,
              danger: colors.danger,
            }}
          />
        </ScrollView>
      </Screen>
    );
  }

  return (
    <Screen>
      <ModalHeader title="Account" onClose={() => router.back()} />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: spacing.lg, gap: spacing.lg }}
      >
        {!configured ? (
          <CaptionText color={colors.muted}>
            {authError ||
              "Accounts aren't set up for this build yet — your counters stay on this device."}
          </CaptionText>
        ) : (
          <>
            <CaptionText color={colors.muted}>
              Sign in to sync the counters on this device to your account, or
              create an account to sync across every device. Creating an account
              is free — syncing across devices is {DURANCE_MONTHLY_PRICE_TEXT}
              /month after a {DURANCE_TRIAL_DAYS}-day free trial.
            </CaptionText>
            <AuthForm onSuccess={() => router.back()} />
          </>
        )}
        <RuleLine />
        <AppVersionPanel
          colors={{
            text: colors.ink,
            muted: colors.muted,
            border: colors.rule,
            accent: colors.accent,
            onAccent: colors.accentInverse,
            danger: colors.danger,
          }}
        />
      </ScrollView>
    </Screen>
  );
}
